import {
  suggestTenantSlug,
  TenantStatus,
  type AddressForm,
  type CreateTenantRequest,
  type PersonNameOrder,
  type PersonNameParts,
  type TenantOperator,
  type UpdateTenantRequest,
} from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type TenantFormValues = {
  id?: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended';
  isInstallationHome: boolean;
  accentHex: string;
  entraTenantId: string;
  entraClientId: string;
  hasLogo: boolean;
  entraConfigured: boolean;
  addressForm: AddressForm;
  personNameParts: PersonNameParts;
  personNameOrder: PersonNameOrder;
};

type TenantFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  initial: TenantFormValues | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: {
    create?: CreateTenantRequest;
    update?: UpdateTenantRequest;
    logoFile?: File | null;
    clearLogo?: boolean;
  }) => Promise<void>;
};

export function toTenantFormValues(item: TenantOperator): TenantFormValues {
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    status: item.status,
    isInstallationHome: item.isInstallationHome,
    accentHex: item.accentHex ?? '',
    entraTenantId: item.entraTenantId ?? '',
    entraClientId: item.entraClientId ?? '',
    hasLogo: Boolean(item.logoStorageKey),
    entraConfigured: item.entraConfigured,
    addressForm: item.addressForm,
    personNameParts: item.personNameParts,
    personNameOrder: item.personNameOrder,
  };
}

export function TenantFormDialog({
  open,
  mode,
  initial,
  busy,
  onOpenChange,
  onSubmit,
}: TenantFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {open ? (
          <TenantFormFields
            key={`${mode}:${initial?.id ?? 'new'}`}
            mode={mode}
            initial={initial}
            busy={busy}
            onOpenChange={onOpenChange}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TenantFormFields({
  mode,
  initial,
  busy,
  onOpenChange,
  onSubmit,
}: Omit<TenantFormDialogProps, 'open'>) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(mode === 'edit');
  const [status, setStatus] = useState<'active' | 'suspended'>(initial?.status ?? 'active');
  const [accentHex, setAccentHex] = useState(initial?.accentHex ?? '');
  const [addressForm, setAddressForm] = useState<AddressForm>(initial?.addressForm ?? 'sie');
  const [personNameParts, setPersonNameParts] = useState<PersonNameParts>(
    initial?.personNameParts ?? 'given_family',
  );
  const [personNameOrder, setPersonNameOrder] = useState<PersonNameOrder>(
    initial?.personNameOrder ?? 'given_family',
  );
  const [entraTenantId, setEntraTenantId] = useState(initial?.entraTenantId ?? '');
  const [entraClientId, setEntraClientId] = useState(initial?.entraClientId ?? '');
  const [entraClientSecret, setEntraClientSecret] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [clearLogo, setClearLogo] = useState(false);

  const homeLocked = mode === 'edit' && initial?.isInstallationHome === true;
  const canSubmit = name.trim().length >= 2 && slug.trim().length >= 2;

  return (
    <>
      <DialogHeader>
        <DialogTitle>{mode === 'create' ? 'Mandant anlegen' : 'Mandant bearbeiten'}</DialogTitle>
        <DialogDescription>
          Name, Host-Slug und Anmeldung dieser Firma. Das Entra-Geheimnis wird nur geschrieben, nie
          angezeigt.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="tenant-name">Name</Label>
          <Input
            id="tenant-name"
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              if (!slugTouched) {
                setSlug(suggestTenantSlug(next));
              }
            }}
            autoComplete="off"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-slug">Slug (Host)</Label>
          <Input
            id="tenant-slug"
            value={slug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value.toLowerCase());
            }}
            autoComplete="off"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Erreichbar unter {'{slug}'}.{'{TENANT_BASE_HOST}'}. Nur Kleinbuchstaben, Ziffern und
            Bindestriche.
          </p>
        </div>
        {mode === 'edit' && (
          <div className="space-y-1.5">
            <Label htmlFor="tenant-status">Status</Label>
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as 'active' | 'suspended')}
              disabled={homeLocked}
            >
              <SelectTrigger id="tenant-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TenantStatus.ACTIVE}>Aktiv</SelectItem>
                <SelectItem value={TenantStatus.SUSPENDED}>Gesperrt</SelectItem>
              </SelectContent>
            </Select>
            {homeLocked ? (
              <p className="text-xs text-muted-foreground">
                Der Heimat-Mandant kann nicht gesperrt werden.
              </p>
            ) : null}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="tenant-address">Anrede</Label>
          <Select
            value={addressForm}
            onValueChange={(value) => setAddressForm(value as AddressForm)}
          >
            <SelectTrigger id="tenant-address">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sie">Sie</SelectItem>
              <SelectItem value="du">Du</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-name-parts">Name</Label>
          <Select
            value={personNameParts}
            onValueChange={(value) => setPersonNameParts(value as PersonNameParts)}
          >
            <SelectTrigger id="tenant-name-parts">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="given">Nur Vorname</SelectItem>
              <SelectItem value="given_family">Vor- und Nachname</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-name-order">Reihenfolge</Label>
          <Select
            value={personNameOrder}
            onValueChange={(value) => setPersonNameOrder(value as PersonNameOrder)}
            disabled={personNameParts === 'given'}
          >
            <SelectTrigger id="tenant-name-order">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="given_family">Vorname Nachname</SelectItem>
              <SelectItem value="family_given">Nachname, Vorname</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-accent">Akzentfarbe</Label>
          <Input
            id="tenant-accent"
            value={accentHex}
            onChange={(event) => setAccentHex(event.target.value)}
            placeholder="#0f4c81"
            autoComplete="off"
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">Sechsstelliger Hex-Wert oder leer lassen.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-logo">Logo</Label>
          <Input
            id="tenant-logo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(event) => {
              setClearLogo(false);
              setLogoFile(event.target.files?.[0] ?? null);
            }}
          />
          {mode === 'edit' && initial?.hasLogo && !clearLogo ? (
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>Ein Logo ist hinterlegt{logoFile ? ' (wird ersetzt)' : ''}.</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setLogoFile(null);
                  setClearLogo(true);
                }}
              >
                Entfernen
              </Button>
            </div>
          ) : null}
          {clearLogo ? (
            <p className="text-xs text-muted-foreground">Logo wird beim Speichern entfernt.</p>
          ) : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-entra-tid">Entra-Tenant-Id</Label>
          <Input
            id="tenant-entra-tid"
            value={entraTenantId}
            onChange={(event) => setEntraTenantId(event.target.value)}
            autoComplete="off"
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-entra-cid">Entra-Client-Id</Label>
          <Input
            id="tenant-entra-cid"
            value={entraClientId}
            onChange={(event) => setEntraClientId(event.target.value)}
            autoComplete="off"
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tenant-entra-secret">Entra-Geheimnis</Label>
          <Input
            id="tenant-entra-secret"
            type="password"
            value={entraClientSecret}
            onChange={(event) => setEntraClientSecret(event.target.value)}
            autoComplete="new-password"
            placeholder={
              mode === 'edit' && initial?.entraConfigured
                ? 'Gesetzt — leer lassen zum Behalten'
                : undefined
            }
          />
          <p className="text-xs text-muted-foreground">
            Wird verschlüsselt gespeichert und nie zurückgelesen.
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button
          type="button"
          disabled={busy || !canSubmit}
          onClick={() => {
            const accent =
              accentHex.trim() === '' ? (mode === 'create' ? undefined : null) : accentHex.trim();
            const entraTid = entraTenantId.trim() || null;
            const entraCid = entraClientId.trim() || null;
            const secret = entraClientSecret.trim() || undefined;
            if (mode === 'create') {
              void onSubmit({
                create: {
                  name: name.trim(),
                  slug: slug.trim(),
                  accentHex: accent === null ? undefined : accent,
                  entraTenantId: entraTid,
                  entraClientId: entraCid,
                  entraClientSecret: secret,
                  addressForm,
                  personNameParts,
                  personNameOrder,
                },
                logoFile,
              });
              return;
            }
            void onSubmit({
              update: {
                name: name.trim(),
                slug: slug.trim(),
                status: homeLocked ? undefined : status,
                accentHex: accent,
                entraTenantId: entraTid,
                entraClientId: entraCid,
                entraClientSecret: secret,
                addressForm,
                personNameParts,
                personNameOrder,
              },
              logoFile,
              clearLogo,
            });
          }}
        >
          {mode === 'create' ? 'Anlegen' : 'Speichern'}
        </Button>
      </DialogFooter>
    </>
  );
}
